import React from "react";
import { Checkbox, DropdownButton, Flex, TableCell, TableRow, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { docTypeLabel, fileIconType, type EffectiveDoc } from "#utils/classificationGrouping";
import * as styles from "./styles_vanilla.css";

export type ReviewRowProps = {
  doc: EffectiveDoc;
  /** True when the row sits in the "Not classified" bucket. Switches the
   *  overflow menu's first action between Assign (unclassified) and Move. */
  needsClassify: boolean;
  /** Renders the leading selection checkbox (unclassified bucket only). */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (doc: EffectiveDoc) => void;
  onAssign: (doc: EffectiveDoc) => void;
  onMove: (doc: EffectiveDoc) => void;
  onEditDocType: (doc: EffectiveDoc) => void;
  onRemove: (doc: EffectiveDoc) => void;
  disabled?: boolean;
};

/**
 * ReviewRow — one document on the classification confirm screen. File glyph +
 * filename with the document type as a subtitle, plus an overflow menu driving
 * the assign / move / edit-type / remove modals. Classification happens through
 * those modals rather than an inline editor.
 */
export const ReviewRow: React.FC<ReviewRowProps> = ({
  doc,
  needsClassify,
  selectable,
  selected,
  onToggleSelect,
  onAssign,
  onMove,
  onEditDocType,
  onRemove,
  disabled,
}) => {
  const subtitle = docTypeLabel(doc.documentType);

  return (
    <TableRow data-file-id={doc.id}>
      {selectable && (
        <TableCell className={styles.selectCell}>
          <Checkbox
            checked={!!selected}
            onChange={() => onToggleSelect?.(doc)}
            disabled={disabled}
            aria-label={`Select ${doc.filename}`}
          />
        </TableCell>
      )}
      <TableCell fill>
        <Flex alignItems="center" gap={Sizes.SM}>
          <Icon type={fileIconType(doc.filename)} variant="solid" aria-hidden="true" />
          <Flex flexDirection="column" className={styles.fileNameColumn}>
            <Text asSpan lines={1} title={doc.filename}>
              {doc.filename}
            </Text>
            {subtitle && (
              <Text asSpan variant="secondary-sm" lines={1}>
                {subtitle}
              </Text>
            )}
          </Flex>
        </Flex>
      </TableCell>
      <TableCell rightAligned className={styles.actionCell}>
        <DropdownButton
          icon="ellipsis"
          variant="muted"
          align="right"
          usePortal
          dropdownClassName={styles.portaledMenu}
          disabled={disabled}
          aria-label={`Actions for ${doc.filename}`}
          data-testid="reviewRowActions"
        >
          {needsClassify ? (
            <DropdownButton.Action
              icon="file-import"
              eventKey="assign"
              onSelect={() => onAssign(doc)}
            >
              Assign to deliverable package
            </DropdownButton.Action>
          ) : (
            <DropdownButton.Action
              icon="file-import"
              eventKey="move"
              onSelect={() => onMove(doc)}
            >
              Move to another package
            </DropdownButton.Action>
          )}
          <DropdownButton.Action
            icon="pen"
            eventKey="edit-doc-type"
            onSelect={() => onEditDocType(doc)}
          >
            Edit document type
          </DropdownButton.Action>
          <DropdownButton.Action icon="trash" eventKey="remove" onSelect={() => onRemove(doc)}>
            Remove
          </DropdownButton.Action>
        </DropdownButton>
      </TableCell>
    </TableRow>
  );
};
